import { DropdownActionItem, DropdownActions, DropdownItemIcon } from '../../../../../../widgets/dropdown';
import { ConfirmDropdownItem } from '../../../configurator-components/confirmTwice';
import { ComposerMenuHint } from './composerMenuHint';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC } from 'react';
import { stringFormat, T } from 'src/shared/helpers';
import GroupIcon from 'src/shared/material_icons/file/folder.svg';
import VisibilityIcon from 'src/shared/material_icons/action/visibility.svg';
import VisibilityOffIcon from 'src/shared/material_icons/action/visibility_off.svg';
import RemoveIcon from 'src/shared/material_icons/action/delete.svg';
import LinkIcon from 'src/shared/material_icons/content/link.svg';
import DuplicateIcon from 'src/shared/material_icons/content/content_copy.svg';
import PreSetIcon from 'src/shared/material_icons/content/inventory_2.svg';
import AttributeIcon from 'src/shared/material_icons/image/tune.svg';
import StepIcon from 'src/shared/material_icons/places/stairs.svg';
import AreaIcon from 'src/shared/material_icons/maps/category.svg';
import MoreIcon from 'src/shared/material_icons/navigation/more_vert.svg';
import RetargetIcon from 'src/shared/material_icons/maps/my_location.svg';
import OptionIcon from 'src/shared/material_icons/image/image.svg';
import ImageIcon from 'src/shared/material_icons/image/add_photo_alternate.svg';
import AddIcon from 'src/shared/material_icons/content/add.svg';
import MergeIcon from 'src/shared/material_icons/maps/merge.svg';
import MoveToIcon from 'src/shared/material_icons/file/drive_file_move.svg';
import CopyToIcon from 'src/shared/material_icons/content/copy_all.svg';
import styled, { css } from 'styled-components';
import { markMatches, TreeTwisty, TreeTwistySlot } from '../../../configurator-components/TreeComponent';
import { ComposerRowActionButton as RowActionButton } from '../../../configurator-components/composer-editors/composerWorkspace';
import { ComposerAttributeNode, ComposerAttributeType } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { getComposerAttributeValidationMessages } from '../helpers/composerAttributeValidation';
import { getComposerAttributeIndent } from '../helpers/composerTreeIndent';
import { isComposerInlineRenameClick } from '../helpers/composerInlineRename';
import { useInlineRename } from '../../../configurator-components/useInlineRename';
import ComposerValidationTooltip from './composerValidationTooltip';
import { TREE_CARD_BACKGROUND, TREE_CARD_RADIUS } from '../../../configurator-components/treeRowLook';

export type ComposerRowAction = 'add' | 'links' | 'duplicate' | 'move' | 'copy' | 'group' | 'merge' | 'image' | 'retarget' | 'remove';

interface ComposerAttributeTreeElementContextMenuData {
    onContextMenuItemClick: (event: React.MouseEvent<HTMLDivElement>, attribute: ComposerAttributeNode) => void;
    contextMenu: React.ReactNode;
}

interface ComposerAttributeTreeElementProps {
    attribute: ComposerAttributeNode;
    onSelect: (event: React.MouseEvent<HTMLDivElement>, attribute: ComposerAttributeNode) => void;
    onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>, attribute: ComposerAttributeNode) => void;
    onToggleExpand?: (attribute: ComposerAttributeNode) => void;
    onMoveUp?: (attribute: ComposerAttributeNode) => void;
    onMoveDown?: (attribute: ComposerAttributeNode) => void;
    /** La riga intera si trascina (utente, 2026-10-15): il gesto lo conduce chi ospita l'albero (`startPointerReorder`). */
    onRowPointerDown?: (event: React.PointerEvent<HTMLElement>, attribute: ComposerAttributeNode) => void;
    onAction?: (action: ComposerRowAction, attribute: ComposerAttributeNode) => void;
    /** La riga è scelta insieme ad altre: il «…» parla della selezione intera. */
    selection?: { count: number; canClone: boolean; canRetarget: boolean; canGroup: boolean; canMerge: boolean; canImage: boolean };
    onVisibilityClick?: (attribute: ComposerAttributeNode) => void;
    contextMenu?: ComposerAttributeTreeElementContextMenuData;
    selected: boolean;
    /** La riga contiene la selezione — un gruppo, o l'attributo di un'opzione scelta: arancio, senza filo. */
    trail?: boolean;
    hasLink?: boolean;
    expanded?: boolean;
    /** Il testo cercato, per accendere nel nome la parte che corrisponde. */
    highlight?: string;
    shift?: number;
    type: ComposerAttributeType;
    areaCount?: number;
    children?: React.ReactNode;
    /**
     * IL NOME SI CAMBIA DOVE SI LEGGE, come nell'Outliner dello Scene Editor: un secondo clic sul
     * nome di una riga già scelta lo apre in scrittura. Chi non lo passa — o lo toglie quando la
     * scelta è un mucchio — lascia il nome da leggere e basta.
     */
    onRename?: (attribute: ComposerAttributeNode, name: string) => void;
}

type ComposerAttributeSubTreeElementProps = Omit<ComposerAttributeTreeElementProps, 'type' | 'children' | 'expanded'>;

const RowIcon = styled.span<{ $kind: 'group' | 'attribute' | 'option' | 'neutral' }>`
    display: inline-flex;
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    color: ${(props) => {
        if (props.$kind === 'group') return 'var(--composer-kind-group, #7040d0)';
        if (props.$kind === 'option') return 'var(--composer-kind-option, #b32d7d)';
        if (props.$kind === 'attribute') return 'var(--composer-kind-attribute, #0e7c7c)';
        return 'var(--composer-muted, #5c6b7a)';
    }};

    svg {
        width: 17px;
        height: 17px;
        fill: currentColor;
    }

    /* L'IMMAGINE DEL GRUPPO AL POSTO DEL GLIFO (utente, 2026-10-16), come fanno le opzioni: è la faccia con
       cui il cliente lo vede nel configuratore, e nell'albero lo si riconosce prima dal disegno che dal nome. */
    img {
        width: 100%;
        height: 100%;
        border-radius: 3px;
        object-fit: cover;
    }
`;

/**
 * IL SEGNO DI UNA RIGA DELLA STRUTTURA: l'immagine, dove il Backoffice la salva — un gruppo —, altrimenti il
 * glifo della specie. Attributi e passi un'immagine loro non ce l'hanno: l'attributo la mette sulle opzioni.
 */
const structureGlyph = (attribute: ComposerAttributeNode) => {
    if (attribute.type === ComposerAttributeType.AttributeGroup && attribute.img) return <img src={attribute.img} alt='' />;
    const isArea = attribute.type === ComposerAttributeType.Attribute && attribute.attributeType === MPlaza.AttributeType.CustomizableArea;
    return isArea ? <AreaIcon /> : getTypeIcon(attribute.type);
};

const Row = styled.div<{
    $shift: number;
    $selected: boolean;
    $trail: boolean;
}>`
    position: relative;
    display: flex;
    min-width: 0;
    /* 30px COME LE RIGHE DELLO SCENE EDITOR (prototipo, utente 2026-10-20). */
    height: 30px;
    align-items: center;
    gap: 4px;
    /* IL RIENTRO STA DENTRO LA RIGA (prototipo, .tree-block .row; utente 2026-10-21): fondo e filo della riga scelta
       vanno da un bordo all'altro della card, il nome si sposta col livello. */
    padding: 0 6px 0 ${(props) => getComposerAttributeIndent(props.$shift) + 6}px;
    color: var(--composer-fg, #1e2a38);
    background: ${(props) => (props.$selected || props.$trail ? 'var(--composer-accent-wash, #fdf0e6)' : 'transparent')};
    /* LA RIGA SCELTA HA IL FILO, la sua scia no (treeRowLook): così si vede quale è, e dove sta. */
    box-shadow: ${(props) => (props.$selected ? 'inset 0 0 0 1px var(--composer-accent, #e8650a)' : 'none')};
    border: 0;
    border-radius: 8px;
    cursor: pointer;
    outline: none;
    user-select: none;
    transition:
        background 110ms ease,
        box-shadow 110ms ease;

    &:hover,
    &:focus-visible {
        background: ${(props) => (props.$selected || props.$trail ? 'var(--composer-accent-wash, #fdf0e6)' : 'var(--composer-hover, #e9eff5)')};
    }

    /* Nella scia il glifo prende l'arancio (la freccetta lo prende da sé); la specie resta nel glifo delle righe ferme. */
    ${(props) =>
        props.$trail &&
        css`
            ${RowIcon} {
                color: var(--composer-accent-text, #a8480b);
            }
        `}

    /* Row commands appear on hover, except the ones marked persistent: a constraint and a disabled row must read at a glance. */
    > div:last-child > *:not([data-persistent]) {
        opacity: 0;
        transition: opacity 110ms ease;
    }

    /* :focus-within alone would keep them lit after a plain click, since the row itself takes focus. */
    &:hover > div:last-child > *,
    &:focus-within:not(:focus) > div:last-child > * {
        opacity: 1;
    }
`;

// Il blocco dell'attributo e delle sue righe: è il recinto entro cui la sua riga resta ferma in cima.
const StickyBlock = styled.div`
    display: flex;
    min-width: 0;
    flex-direction: column;
`;

// L'ATTRIBUTO RESTA FERMO IN CIMA mentre le sue righe scorrono (utente, 2026-10-15; prototipo
// .tree-block > .row:first-child), fino alla fine del suo blocco. Ferma è una FASCIA larga quanto la
// colonna, piena del colore del posto in cui sta, che copre anche l'aria sopra: la sola riga, coi suoi
// margini, sembrava sospesa sulle opzioni che le passavano intorno. Un filo la stacca da ciò che scorre.
//
// LA FASCIA C'È SOLO DA FERMA (utente, 2026-10-15). Gli otto pixel che coprono l'aria sopra la riga si
// dipingevano anche quando la riga stava al suo posto, e tagliavano il fondo della riga di sopra — un
// attributo vuoto, scelto, perdeva il bordo inferiore appena si apriva quello sotto. Chi è fermo lo dice
// l'IntersectionObserver: con `top: -1px` la riga ferma esce di un pixel dal bordo dell'elenco.
const StickyHead = styled.div<{ $surface: string; $stuck: boolean }>`
    position: sticky;
    top: -1px;
    z-index: 3;
    background: ${(props) => props.$surface};
    box-shadow: ${(props) => (props.$stuck ? `0 -8px 0 ${props.$surface}, 0 1px 0 var(--composer-line, #dbe2e6)` : 'none')};
    margin-bottom: 1px;
`;

const StickyRow: FC<{ surface: string; children: React.ReactNode }> = ({ surface, children }) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [stuck, setStuck] = React.useState(false);
    React.useEffect(() => {
        const element = ref.current;
        if (!element) return;
        const observer = new IntersectionObserver(
            ([entry]) => setStuck(entry.intersectionRatio > 0 && entry.boundingClientRect.top < entry.intersectionRect.top),
            { threshold: [0, 1] }
        );
        observer.observe(element);
        return () => observer.disconnect();
    }, []);
    return (
        <StickyHead ref={ref} $surface={surface} $stuck={stuck}>
            {children}
        </StickyHead>
    );
};

// SPENTA SI SBIADISCONO IL SEGNO E IL NOME (prototipo, come nello Scene Editor): freccetta e conteggio restano.
const RowMain = styled.div<{ $muted: boolean }>`
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: center;
    gap: 4px;

    ${(props) =>
        props.$muted &&
        css`
            > [data-row-glyph],
            > [data-row-name] {
                opacity: 0.45;
            }
        `}
`;

const RowName = styled.span<{ $section?: boolean }>`
    min-width: 0;
    flex: 0 1 auto;
    overflow: hidden;
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;

    /* IL GRUPPO È UN'ETICHETTA DI SEZIONE, in maiuscoletto come nel prototipo (.row.is-section;
       utente, 2026-10-15): raggruppa, non si configura. Il nome resta quello scritto: lo cambia solo la vista. */
    ${(props) =>
        props.$section &&
        css`
            color: var(--composer-muted, #5c6b7a);
            font-size: 11px;
            letter-spacing: 0.06em;
            text-transform: uppercase;
        `}
`;

const RowNameInput = styled.input`
    min-width: 0;
    flex: 1 1 auto;
    margin: -2px -5px;
    padding: 1px 4px;
    color: var(--composer-fg, #263238);
    background: var(--composer-hover, #eceff1);
    border: 1px solid var(--composer-accent, #e8650a);
    border-radius: 4px;
    font: inherit;
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    outline: none;
`;

const RowCount = styled.span`
    flex: 0 0 auto;
    color: var(--composer-faint, #677078);
    font-size: 10px;
    font-variant-numeric: tabular-nums;
`;

const RowActions = styled.div`
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 1px;
`;

// Il posto di un comando che la riga non ha, così i comandi restano incolonnati.
const Slot = styled.span`
    display: inline-flex;
    width: 24px;
    height: 24px;
`;

// DUE PIXEL FRA UNA RIGA E L'ALTRA, come nello Scene Editor (utente, 2026-10-21): due opzioni scelte vicine restano due.
const Children = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

// UNA CARD GRIGIA PER OGNI ATTRIBUTO, TESTATA COMPRESA (prototipo `.tree-block`; utente, 2026-10-20 — prima il grigio
// cominciava dai figli del ramo, una card sola per gruppo). Si vede dove finisce un attributo e comincia il successivo;
// il gruppo resta un'etichetta sul pannello. È anche il recinto entro cui la riga dell'attributo resta ferma in cima.
//
// LE RIGHE STANNO A 2PX DAL BORDO DELLA CARD, e la testata è staccata dalle opzioni da 6px e un filo (prototipo,
// .tree-block > .row:first-child; utente 2026-10-21). Chiuso l'attributo, il filo se ne va con le opzioni.
const AttributeCard = styled.div`
    display: flex;
    min-width: 0;
    flex-direction: column;
    margin: 2px 0 6px;
    padding: 2px;
    background: ${TREE_CARD_BACKGROUND};
    border-radius: calc(${TREE_CARD_RADIUS} + 2px);

    /* LA TESTATA DELLA CARD È UN'ETICHETTA DELLE SUE OPZIONI (prototipo, .tree-block > .row:first-child): 28px, nome a
       12px grigio e glifo a 13. Sono le opzioni il contenuto. */
    > [data-composer-row]:first-child,
    > :first-child > [data-composer-row] {
        height: 28px;

        [data-row-name] {
            font-size: 12px;
            color: var(--editor-muted, #55595f);
        }

        [data-row-glyph] svg {
            width: 13px;
            height: 13px;
        }
    }

    > :first-child:not(:last-child) {
        margin-bottom: 6px;

        &::after {
            position: absolute;
            right: 8px;
            bottom: -4px;
            left: 8px;
            height: 1px;
            background: var(--editor-line, #d1d3d8);
            content: '';
            pointer-events: none;
        }
    }
`;

const getKind = (type: ComposerAttributeType) => {
    if (type === ComposerAttributeType.Attribute) return 'attribute' as const;
    if (type === ComposerAttributeType.AttributeGroup) return 'group' as const;
    return 'neutral' as const;
};

const getTypeLabel = (type: ComposerAttributeType) => {
    if (type === ComposerAttributeType.Attribute) return T._('Attribute', 'Admin');
    if (type === ComposerAttributeType.AttributeGroup) return T._('Group', 'Admin');
    if (type === ComposerAttributeType.AttributeStep) return T._('Step', 'Admin');
    return T._('Preset', 'Admin');
};

const getTypeIcon = (type: ComposerAttributeType) => {
    if (type === ComposerAttributeType.Attribute) return <AttributeIcon />;
    if (type === ComposerAttributeType.AttributeGroup) return <GroupIcon />;
    if (type === ComposerAttributeType.AttributeStep) return <StepIcon />;
    return <PreSetIcon />;
};

/**
 * L'ICONA COLORATA DI UNA RIGA, FUORI DALL'ALBERO: l'elenco del badge «N selected» la mette accanto a
 * ogni voce, come lo Scene Editor, perché un mucchio può essere fatto di cose diverse.
 */
export const ComposerAttributeMark: FC<{ attribute: ComposerAttributeNode }> = ({ attribute }) => (
    <RowIcon $kind={getKind(attribute.type)}>{structureGlyph(attribute)}</RowIcon>
);

export const ComposerAreaMark: FC = () => (
    <RowIcon $kind='attribute'>
        <AreaIcon />
    </RowIcon>
);

export const ComposerOptionMark: FC = () => (
    <RowIcon $kind='option'>
        <OptionIcon />
    </RowIcon>
);

export const ComposerAttributeTreeElement: FC<ComposerAttributeTreeElementProps> = ({
    attribute,
    onSelect,
    onDoubleClick,
    onToggleExpand,
    contextMenu,
    selected,
    trail = false,
    hasLink,
    shift = 0,
    expanded,
    highlight,
    type,
    areaCount,
    children,
    onMoveUp,
    onMoveDown,
    onRowPointerDown,
    onAction,
    selection,
    onVisibilityClick,
    onRename
}) => {
    // Secondo clic, Invio, Esc e uscita dal campo: lo stesso gesto delle righe dello Scene Editor.
    const rename = useInlineRename();
    const validationMessages = getComposerAttributeValidationMessages(attribute);
    const hasWarnings = validationMessages.length > 0;
    const isAreaAttribute = type === ComposerAttributeType.Attribute && attribute.attributeType === MPlaza.AttributeType.CustomizableArea;
    const itemCount = isAreaAttribute ? (areaCount ?? 0) : type === ComposerAttributeType.Attribute ? attribute.options.length : React.Children.count(children);
    const countLabel = stringFormat(
        isAreaAttribute
            ? itemCount === 1
                ? T._('{0} area', 'Admin')
                : T._('{0} areas', 'Admin')
            : itemCount === 1
              ? T._('{0} item', 'Admin')
              : T._('{0} items', 'Admin'),
        itemCount.toString()
    );
    const enabled = attribute.enabled !== false;
    const typeLabel = isAreaAttribute ? T._('Customizable area', 'Admin') : getTypeLabel(type);
    // Resta ferma in cima la riga di un attributo aperto: le sue righe le scorrono sotto. Il fondo è quello
    // del posto in cui sta — la colonna bianca al primo livello, la card grigia dentro un gruppo.
    const sticky = type === ComposerAttributeType.Attribute && !!children && !expanded;
    const ownCard = type === ComposerAttributeType.Attribute;
    const stickySurface = sticky ? (ownCard ? TREE_CARD_BACKGROUND : 'var(--composer-panel, #fff)') : undefined;
    // IL «+» DELLA RIGA AGGIUNGE UN FIGLIO (utente, 2026-10-15; prototipo): un'opzione all'attributo, un'area
    // all'attributo delle aree, qualcosa dentro un gruppo o un passo. Un preset non ha figli da aggiungere qui.
    const addLabel =
        type === ComposerAttributeType.PreSetContainer
            ? undefined
            : isAreaAttribute
              ? T._('Add an area', 'Admin')
              : type === ComposerAttributeType.Attribute
                ? T._('Add an option', 'Admin')
                : T._('Add inside', 'Admin');
    // Group e Merge valgono per la riga o per la selezione di cui fa parte.
    // GROUP, MERGE E L'IMMAGINE DI ANTEPRIMA VALGONO PER UNA SELEZIONE (utente, 2026-10-15): sulla riga sola
    // un gruppo di una cosa e una fusione con niente non hanno senso, e l'immagine di un attributo solo
    // si cambia nelle sue Settings. Nel «…» compaiono solo se la riga è scelta insieme ad altre.
    const canGroup = !!selection?.canGroup;
    const canMerge = !!selection?.canMerge;

    const row = (
        <Row
            $shift={shift}
            $selected={selected}
            $trail={trail && !selected}
            data-composer-row={attribute.id}
            data-reorder-id={attribute.id}
            onPointerDown={onRowPointerDown ? (event) => onRowPointerDown(event, attribute) : undefined}
            data-reorder-level={shift}
            aria-expanded={children ? !expanded : undefined}
            aria-selected={selected}
            onClick={(event) => onSelect(event, attribute)}
            onContextMenu={(event) => {
                if (!contextMenu) return;
                event.preventDefault();
                event.stopPropagation();
                contextMenu.onContextMenuItemClick(event, attribute);
            }}
            onDoubleClick={onDoubleClick ? (event) => onDoubleClick(event, attribute) : undefined}
            onKeyDown={(event) => {
                if (event.key === 'ArrowUp' && event.altKey) {
                    event.preventDefault();
                    onMoveUp?.(attribute);
                }
                if (event.key === 'ArrowDown' && event.altKey) {
                    event.preventDefault();
                    onMoveDown?.(attribute);
                }
            }}
            role='treeitem'
            tabIndex={0}
        >
            <RowMain $muted={!enabled}>
                {children ? (
                    <ZTooltip title={!expanded ? T._('Collapse', 'Admin') : T._('Expand', 'Admin')}>
                        <TreeTwisty
                            type='button'
                            $isOpen={!expanded}
                            $trail={trail && !selected}
                            aria-label={!expanded ? T._('Collapse', 'Admin') : T._('Expand', 'Admin')}
                            onClick={(event) => {
                                event.stopPropagation();
                                onToggleExpand?.(attribute);
                            }}
                        />
                    </ZTooltip>
                ) : (
                    <TreeTwistySlot aria-hidden='true' />
                )}
                <ZTooltip title={typeLabel}>
                    <RowIcon $kind={getKind(type)} data-reorder-anchor data-row-glyph>
                        {structureGlyph(attribute)}
                    </RowIcon>
                </ZTooltip>
                {rename.editingId === attribute.id && onRename ? (
                    <RowNameInput aria-label={T._('Name', 'Admin')} {...rename.fieldProps(attribute.name, (value) => onRename(attribute, value))} />
                ) : (
                    <ZTooltip title={attribute.name} position='top' isOverflowEnabled>
                        <RowName
                            data-row-name
                            $section={type === ComposerAttributeType.AttributeGroup}
                            onClick={(event) => {
                                // IL SECONDO CLIC, NON IL DOPPIO: il doppio clic apre le opzioni, e un nome
                                // aperto in scrittura sotto il cursore lo intercetterebbe.
                                if (!onRename || !selected || !isComposerInlineRenameClick(event)) return;

                                event.stopPropagation();
                                rename.start(attribute.id, attribute.name);
                            }}
                        >
                            {markMatches(attribute.name, highlight)}
                        </RowName>
                    </ZTooltip>
                )}
                <RowCount aria-label={countLabel}>{itemCount}</RowCount>
                {hasWarnings && <ComposerValidationTooltip messages={validationMessages} />}
            </RowMain>
            <RowActions>
                {addLabel && (
                    <ZTooltip title={addLabel}>
                        <RowActionButton
                            aria-label={addLabel}
                            onClick={(event) => {
                                event.stopPropagation();
                                onAction?.('add', attribute);
                            }}
                        >
                            <AddIcon />
                        </RowActionButton>
                    </ZTooltip>
                )}
                {hasLink ? (
                    <ZTooltip title={T._('This item has constraints', 'Admin')}>
                        <RowActionButton
                            $active
                            aria-label={T._('Open constraints', 'Admin')}
                            data-persistent='true'
                            onClick={(event) => {
                                event.stopPropagation();
                                onAction?.('links', attribute);
                            }}
                        >
                            <LinkIcon />
                        </RowActionButton>
                    </ZTooltip>
                ) : (
                    <Slot />
                )}
                {type !== ComposerAttributeType.PreSetContainer && (
                    <ZTooltip title={enabled ? T._('Disable this row', 'Admin') : T._('Enable this row', 'Admin')}>
                        <RowActionButton
                            $off={!enabled}
                            aria-label={enabled ? T._('Disable this row', 'Admin') : T._('Enable this row', 'Admin')}
                            aria-pressed={!enabled}
                            data-persistent={enabled ? undefined : 'true'}
                            onClick={(event) => {
                                event.stopPropagation();
                                onVisibilityClick?.(attribute);
                            }}
                        >
                            {enabled ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </RowActionButton>
                    </ZTooltip>
                )}
                <DropdownActions
                    header={
                        <RowActionButton type='button' aria-label={T._('More commands', 'Admin')}>
                            <MoreIcon />
                        </RowActionButton>
                    }
                    sections={[
                        [
                            <DropdownActionItem
                                key='duplicate'
                                disabled={selection ? !selection.canClone : false}
                                icon={<DuplicateIcon />}
                                onClick={() => onAction?.('duplicate', attribute)}
                            >
                                {T._('Clone', 'Admin')}
                            </DropdownActionItem>,
                            // MOVE TO SUBITO PRIMA DI COPY TO (prototipo): dentro un gruppo o un passo. Un gruppo resta dov'è.
                            <DropdownActionItem
                                key='move'
                                disabled={type === ComposerAttributeType.AttributeGroup}
                                icon={<MoveToIcon />}
                                onClick={() => onAction?.('move', attribute)}
                            >
                                {T._('Move to…', 'Admin')}
                            </DropdownActionItem>,
                            <DropdownActionItem
                                key='copy'
                                disabled={type === ComposerAttributeType.AttributeGroup}
                                icon={<CopyToIcon />}
                                onClick={() => onAction?.('copy', attribute)}
                            >
                                {T._('Copy to…', 'Admin')}
                            </DropdownActionItem>,
                            selection && (
                                <DropdownActionItem key='group' disabled={!canGroup} icon={<GroupIcon />} onClick={() => onAction?.('group', attribute)}>
                                    {T._('Group', 'Admin')}
                                </DropdownActionItem>
                            ),
                            selection && (
                                <ComposerMenuHint key='merge' hint={T._('Turn the selected attributes into one, with all their options', 'Admin')}>
                                    <DropdownActionItem key='merge' disabled={!canMerge} icon={<MergeIcon />} onClick={() => onAction?.('merge', attribute)}>
                                        {T._('Merge', 'Admin')}
                                    </DropdownActionItem>
                                </ComposerMenuHint>
                            ),
                            selection && (
                                <ComposerMenuHint key='image' hint={T._('Set the preview image of all their options', 'Admin')}>
                                    <DropdownActionItem
                                        key='image'
                                        disabled={!selection.canImage}
                                        icon={<ImageIcon />}
                                        onClick={() => onAction?.('image', attribute)}
                                    >
                                        {T._('Preview image…', 'Admin')}
                                    </DropdownActionItem>
                                </ComposerMenuHint>
                            ),
                            <ComposerMenuHint key='retarget' hint={T._('Point the actions at other objects and materials', 'Admin')}>
                                <DropdownActionItem
                                    key='retarget'
                                    disabled={
                                        selection
                                            ? !selection.canRetarget
                                            : type !== ComposerAttributeType.Attribute || isAreaAttribute || attribute.options.length === 0
                                    }
                                    icon={<RetargetIcon />}
                                    onClick={() => onAction?.('retarget', attribute)}
                                >
                                    {T._('Retarget', 'Admin')}
                                </DropdownActionItem>
                            </ComposerMenuHint>
                        ],
                        [
                            // Rimuovere chiede due volte, nella voce stessa (confirmTwice).
                            <ConfirmDropdownItem
                                key='remove'
                                icon={
                                    <DropdownItemIcon>
                                        <RemoveIcon />
                                    </DropdownItemIcon>
                                }
                                onConfirm={() => onAction?.('remove', attribute)}
                            >
                                {T._('Remove', 'Admin')}
                            </ConfirmDropdownItem>
                        ]
                    ]}
                    minWidth='236px'
                    orientation='RIGHT'
                />
            </RowActions>
        </Row>
    );

    const content = (
        <>
            {stickySurface ? <StickyRow surface={stickySurface}>{row}</StickyRow> : row}
            {!expanded && children && <Children role='group'>{children}</Children>}
            {contextMenu?.contextMenu}
        </>
    );

    if (ownCard) return <AttributeCard>{content}</AttributeCard>;
    return sticky ? <StickyBlock>{content}</StickyBlock> : content;
};

export const ComposerAttributeSubTreeElement: FC<ComposerAttributeSubTreeElementProps> = ({ attribute, shift = 1, ...props }) => (
    <ComposerAttributeTreeElement {...props} attribute={attribute} shift={shift} type={ComposerAttributeType.Attribute} />
);
